import type { JSX, PropsWithChildren } from 'react';

import { Text } from '@change/ds-core-ui/next';

import type { QrCodeType } from '@change-corgi/middle-end-gql-schema';

import { usePetitionQRCode } from 'src/app/shared/hooks/qrcode';

type PetitionQRCodeDownloadLinkProps = PropsWithChildren<{
	slugOrId: string;
	type: QrCodeType;
	size: number;
	filename?: string;
	onDownload?: () => Promise<void>;
}>;
/**
 * Renders a link to download a QR code with the given value.
 * The QRCode is not visible, but is used to download a hi-res version of the QRCode
 */
export function PetitionQRCodeDownloadLink({
	children,
	slugOrId,
	size,
	type,
	filename,
	onDownload,
}: PetitionQRCodeDownloadLinkProps): JSX.Element {
	const { data } = usePetitionQRCode({ slugOrId, size, type, adjustForPixelRatio: false });

	const handleDownload = async () => {
		await onDownload?.();
	};

	return (
		<>
			{/* We can't use the Link component here because the href becomes the
				page url with data attribute appended, which breaks the download. */}
			{/* eslint-disable-next-line react/forbid-elements */}
			<a
				href={data?.imageBase64Uri ?? '#'}
				download={filename ?? true}
				onClick={handleDownload}
				className="font-bold decoration-solid decoration-[0%] underline-offset-[0%]"
				data-qa="qrcode-download-link"
				data-testid="qrcode-download-link"
			>
				<Text className="text-size-body-bold">{children}</Text>
			</a>
		</>
	);
}
